import { useTranslation } from "react-i18next";

import { CopyKeyButton } from "features/usage-config/components/copy-key-button";
import { Switch } from "components/ui/switch";
import { USAGE_LIMITS_I18N, UsageMeterDefinition } from "features/usage-config/constants/usage-meters";
import { cn } from "lib/utils";

const T = USAGE_LIMITS_I18N;

export interface UsageMeterRowProps {
  meter: UsageMeterDefinition;
  /** Dimmed when nothing on this row is being checked. */
  isDimmed?: boolean;
  toggle?: { checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void };
  status: string;
}

// Every column but the description is a fixed width, so the rows line up whether or not
// a description wraps.
export const UsageMeterRow = ({ meter, isDimmed, toggle, status }: UsageMeterRowProps) => {
  const { t } = useTranslation();

  return (
    <div className={cn("flex items-center gap-4 py-3", isDimmed && "opacity-60")}>
      {toggle && (
        <Switch
          checked={toggle.checked}
          disabled={toggle.disabled}
          onCheckedChange={toggle.onChange}
          aria-label={t(meter.label)}
          className="shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{t(meter.label)}</div>
        <div className="text-xs text-muted-foreground">
          {t(meter.description)}{" "}
          <span className="whitespace-nowrap">
            {t(T.UNIT_HINT, { unit: t(T.UNIT_LABELS[meter.unit]) })}.
          </span>
        </div>
      </div>
      {/* Shown even when off: this is the key an operator pastes into Utilities. */}
      <span className="w-44 shrink-0" title={t(T.METER_KEY_HINT)}>
        <span className="flex items-center gap-1">
          <code className="whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {meter.meterKey}
          </code>
          <CopyKeyButton value={meter.meterKey} label={t(T.METER_KEY_COPY)} />
        </span>
      </span>
      <span className="w-36 shrink-0 text-right text-sm text-muted-foreground">{status}</span>
    </div>
  );
};

export interface UsageMeterHeaderProps {
  hasToggle?: boolean;
}

/** Column captions on the same widths as the rows, so headings sit over their column. */
export const UsageMeterHeader = ({ hasToggle }: UsageMeterHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-4 pb-2 text-xs text-muted-foreground">
      {hasToggle && <span className="w-9 shrink-0" />}
      <span className="min-w-0 flex-1">{t(T.WHAT_AGENTS_CHECK)}</span>
      <span className="w-44 shrink-0">{t(T.METER_KEY_COLUMN)}</span>
      <span className="w-36 shrink-0" />
    </div>
  );
};
